import { useEffect, useState, useRef } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { webSocket } from "rxjs/webSocket";
import { interval, Subject } from "rxjs";
import { catchError } from "rxjs/operators";
import AuthPage from "./AuthPage";
import "./App.css";

const COINS = [
  { symbol: "btcusdt", label: "BTC", name: "Bitcoin", color: "#f7931a" },
  { symbol: "ethusdt", label: "ETH", name: "Ethereum", color: "#627eea" },
  { symbol: "solusdt", label: "SOL", name: "Solana", color: "#9945ff" },
  { symbol: "xrpusdt", label: "XRP", name: "Ripple", color: "#00aae4" },
];

const MAX_POINTS = 300;
const WINDOW_MS = 60000;

function formatPrice(price) {
  if (price === undefined || price === null || isNaN(price)) return "-";
  if (price >= 1000) return price.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (price >= 1) return price.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  return price.toLocaleString("en-US", { minimumFractionDigits: 4, maximumFractionDigits: 6 });
}

function CoinChart({ history, color, symbol, startTime, buyPrice }) {
  const now = Date.now();

  let domainMin, domainMax;
  if (startTime) {
    domainMin = startTime;
    domainMax = Math.max(startTime + 15000, now);
  } else {
    domainMin = now - WINDOW_MS;
    domainMax = now;
  }

  let visible = (history || [])
    .filter((p) => p.time >= domainMin && p.time <= domainMax)
    .sort((a, b) => a.time - b.time);

  if (startTime) {
    if (visible.length === 0 && buyPrice) {
      visible = [{ time: startTime, price: buyPrice }];
    } else if (visible.length > 0 && visible[0].time > startTime && buyPrice) {
      visible = [{ time: startTime, price: buyPrice }, ...visible];
    }
  }

  const prices = visible.map((p) => p.price);
  let yMin = "auto";
  let yMax = "auto";
  let delta = 0;

  if (prices.length > 0) {
    const minP = Math.min(...prices);
    const maxP = Math.max(...prices);
    delta = maxP - minP;

    const padding = Math.max(0.005, delta * 0.1);
    yMin = minP - padding;
    yMax = maxP + padding;
  }

  const step = (domainMax - domainMin) / 6;
  const ticks = Array.from({ length: 7 }, (_, i) => Math.round(domainMin + i * step));

  const formatTick = (val) => {
    if (val === undefined || val === null) return "";
    if (delta < 0.05) return val.toFixed(3);
    if (val >= 1000) return val.toFixed(2);
    if (val >= 1) return val.toFixed(2);
    return val.toFixed(4);
  };

  const gradientId = `chart-grad-${symbol}`;

  return (
    <div style={{ width: "100%", height: "100%", position: "relative" }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={visible} margin={{ top: 12, right: 12, bottom: 20, left: 10 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.2} />
              <stop offset="100%" stopColor={color} stopOpacity={0.0} />
            </linearGradient>
          </defs>

          <CartesianGrid vertical={false} stroke="#f1f5f9" strokeDasharray="3 3" />

          <YAxis
            domain={[yMin, yMax]}
            tickCount={5}
            tickFormatter={formatTick}
            tick={{ fill: "#94a3b8", fontSize: 11, fontFamily: "Inter, sans-serif" }}
            tickLine={false}
            axisLine={false}
            orientation="right"
            width={65}
          />

          <XAxis
            dataKey="time"
            type="number"
            scale="time"
            domain={[domainMin, domainMax]}
            ticks={ticks}
            tickFormatter={(ts) => new Date(ts).toLocaleTimeString("en-US", { hour12: false, minute: "2-digit", second: "2-digit" })}
            tick={{ fill: "#94a3b8", fontSize: 11, fontFamily: "Inter, sans-serif", dy: 6 }}
            tickLine={{ stroke: "#e2e8f0" }}
            axisLine={{ stroke: "#e2e8f0" }}
            height={28}
          />

          <Tooltip
            contentStyle={{
              background: "#ffffff",
              border: "1px solid #e2e8f0",
              boxShadow: "0 10px 25px -5px rgba(15, 23, 42, 0.08), 0 8px 10px -6px rgba(15, 23, 42, 0.04)",
              fontSize: 12,
              fontFamily: "Inter, sans-serif",
              borderRadius: 12,
              color: "#0f172a",
              padding: "8px 12px",
              fontWeight: 500,
            }}
            labelFormatter={(ts) => new Date(ts).toLocaleTimeString("en-US", { hour12: false })}
            formatter={(v) => [`$${formatPrice(v)}`, "Price"]}
          />

          <Area
            type="monotone"
            dataKey="price"
            stroke={color}
            strokeWidth={2.5}
            fill={`url(#${gradientId})`}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function CoinCard({ coin, history, expanded, onExpand }) {
  const latest = history.at(-1);
  const price = latest ? formatPrice(latest.price) : "-";
  const prevPrice = history.length > 1 ? history.at(-2)?.price : latest?.price;
  const isUp = latest && prevPrice ? latest.price >= prevPrice : true;

  return (
    <div className="coin-card">
      <div className="coin-card-header">
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span className="coin-badge" style={{ background: coin.color, color: "#ffffff" }}>
            {coin.label}
          </span>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", letterSpacing: "-0.01em" }}>
              {coin.name}
            </span>
            <span style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>
              {coin.symbol.toUpperCase()}
            </span>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ textAlign: "right" }}>
            <div className={`price-display ${isUp ? "price-up" : "price-down"}`}>
              ${price}
            </div>
            <div className="live-status-tag">
              <span className="live-dot" /> LIVE STREAM
            </div>
          </div>

          <button
            onClick={() => onExpand(expanded ? null : coin.symbol)}
            className="expand-btn"
            title={expanded ? "Collapse" : "Expand"}
          >
            {expanded ? "Collapse" : "Expand"}
          </button>
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, marginTop: 8 }}>
        <CoinChart history={history} color={coin.color} symbol={coin.symbol} />
      </div>
    </div>
  );
}

function HeaderBar({ user, isTradeActive, onOpenModal, onOpenProfile }) {
  return (
    <header className="main-header">
      <div className="header-left">
        <button className="btn-profile" onClick={onOpenProfile} title="View Profile & Portfolio">
          <div className="profile-avatar">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </div>
          <span className="profile-username">{user ? user.username : "Profile"}</span>
        </button>
      </div>

      <div className="header-actions">
        <button
          className={`btn-open-trade ${isTradeActive ? "disabled" : ""}`}
          onClick={onOpenModal}
          disabled={isTradeActive}
          title={isTradeActive ? "Close active position to start a new trade" : "Start a new trade"}
        >
          {isTradeActive ? "Position Active" : "+ Start Trade"}
        </button>
      </div>
    </header>
  );
}

function TradeModal({ coins, isOpen, onClose, history, onExecuteTrade }) {
  const [selectedSymbol, setSelectedSymbol] = useState("btcusdt");
  const [amount, setAmount] = useState("500");

  if (!isOpen) return null;

  const selectedCoin = coins.find((c) => c.symbol === selectedSymbol) || coins[0];
  const currentPrice = history[selectedSymbol]?.at(-1)?.price;
  const parsedAmount = parseFloat(amount);
  const validAmount = !isNaN(parsedAmount) && parsedAmount > 0;
  const estimatedCoins = validAmount && currentPrice ? parsedAmount / currentPrice : null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validAmount || !currentPrice) return;
    onExecuteTrade({
      symbol: selectedSymbol,
      amount: parsedAmount,
      price: currentPrice,
      coinsBought: estimatedCoins,
    });
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h3 className="modal-title">Start New Position</h3>
            <p className="modal-subtitle">Select an asset and enter trade allocation</p>
          </div>
          <button className="modal-close" onClick={onClose}>X</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label className="field-label">SELECT COIN</label>
            <div className="asset-grid">
              {coins.map((c) => {
                const active = c.symbol === selectedSymbol;
                const price = history[c.symbol]?.at(-1)?.price;
                return (
                  <button
                    key={c.symbol}
                    type="button"
                    className={`asset-card ${active ? "active" : ""}`}
                    onClick={() => setSelectedSymbol(c.symbol)}
                    style={active ? { background: c.color, color: "#ffffff" } : {}}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span className="dot" style={{ background: active ? "#ffffff" : c.color }} />
                      <span className="asset-code" style={{ color: active ? "#ffffff" : "#0f172a" }}>{c.label}</span>
                    </div>
                    <span className="asset-price" style={{ color: active ? "rgba(255, 255, 255, 0.9)" : "#64748b" }}>
                      ${formatPrice(price)}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="form-group">
            <label className="field-label">INVESTMENT AMOUNT ($)</label>
            <div className="input-wrapper">
              <span className="currency-prefix">$</span>
              <input
                type="number"
                min="1"
                step="any"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="100"
                className="amount-input modal-input"
                autoFocus
              />
            </div>
            <div className="preset-buttons" style={{ marginTop: 8 }}>
              {[100, 500, 1000, 2500].map((val) => (
                <button
                  key={val}
                  type="button"
                  className="preset-btn"
                  onClick={() => setAmount(val.toString())}
                >
                  ${val}
                </button>
              ))}
            </div>
          </div>

          {estimatedCoins && (
            <div className="conversion-badge" style={{ alignSelf: "stretch", textAlign: "center" }}>
              Estimated Position: ~ {estimatedCoins.toFixed(6)} {selectedCoin.label}
            </div>
          )}

          <div className="modal-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn-trade btn-buy"
              disabled={!validAmount || !currentPrice}
            >
              Confirm & Open Position
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ActiveTradeView({ trade, coin, history, currentPrice, pnl, pnlPct, onSell }) {
  const currentValue = trade.coinsBought && currentPrice ? trade.coinsBought * currentPrice : trade.amount;
  const isUp = currentPrice && trade.buyPrice ? currentPrice >= trade.buyPrice : true;

  return (
    <div className="active-trade-wrapper">
      <div className="active-trade-bar">
        <div className="active-coin-header">
          <span className="coin-badge-lg" style={{ background: coin.color, color: "#ffffff" }}>
            {coin.label}
          </span>
          <div className="active-coin-details">
            <h2 className="active-coin-title">{coin.name} Position</h2>
            <span className="active-symbol-tag">{coin.symbol.toUpperCase()} - ACTIVE TRADE STREAM</span>
          </div>
        </div>

        <div className="active-metrics-row">
          <div className="metric-card highlight">
            <span className="metric-title">CURRENT PRICE</span>
            <span className={`metric-value-lg ${isUp ? "text-up" : "text-down"}`}>
              ${formatPrice(currentPrice)}
            </span>
          </div>

          <div className="metric-card">
            <span className="metric-title">ENTRY PRICE</span>
            <span className="metric-value">${formatPrice(trade.buyPrice)}</span>
          </div>

          <div className="metric-card">
            <span className="metric-title">POSITION VALUE</span>
            <span className="metric-value">${formatPrice(currentValue)}</span>
          </div>

          {pnl !== null && pnlPct !== null && (
            <div className={`metric-card pnl-card ${pnl >= 0 ? "profit" : "loss"}`}>
              <div className="pnl-card-header">
                <span className="metric-title">UNREALIZED PnL</span>
                <span className="pnl-pulse-dot" />
              </div>
              <div className="pnl-numbers">
                <span className="pnl-value-lg">{pnl >= 0 ? "+" : ""}${pnl.toFixed(2)}</span>
                <span className="pnl-percentage">({pnlPct >= 0 ? "+" : ""}{pnlPct.toFixed(2)}%)</span>
              </div>
            </div>
          )}
        </div>

        <button className="btn-trade btn-sell btn-close-trade" onClick={onSell}>
          CLOSE POSITION
        </button>
      </div>

      <div className="active-chart-container">
        <CoinChart
          history={history}
          color={coin.color}
          symbol={coin.symbol}
          startTime={trade.startTime}
          buyPrice={trade.buyPrice}
        />
      </div>
    </div>
  );
}

const COIN_COLOR_MAP = {
  btcusdt: "#f7931a",
  ethusdt: "#627eea",
  solusdt: "#9945ff",
  xrpusdt: "#00aae4",
};

function TradeHistoryTable({ tradeHistory }) {
  if (!tradeHistory.length) {
    return (
      <div className="empty-history">
        No executed trades recorded yet. Start a new trade position to build your trade history.
      </div>
    );
  }

  return (
    <div className="history-table-container">
      <div className="history-table-header">
        <span className="history-title">EXECUTED TRADES LOG</span>
        <span className="history-count">{tradeHistory.length} Total</span>
      </div>

      <div className="history-table-scroll-wrapper">
        <table className="history-table">
          <thead>
            <tr>
              <th>COIN</th>
              <th style={{ textAlign: "right" }}>BUY PRICE</th>
              <th style={{ textAlign: "right" }}>SELL PRICE</th>
              <th style={{ textAlign: "right" }}>AMOUNT</th>
              <th style={{ textAlign: "right" }}>PnL ($)</th>
            </tr>
          </thead>
          <tbody>
            {tradeHistory.slice().reverse().map((t, i) => {
              const coinColor = COIN_COLOR_MAP[t.symbol?.toLowerCase()] || "#0f172a";
              return (
                <tr key={i}>
                  <td>
                    <span className="table-coin-badge" style={{ background: coinColor, color: "#ffffff" }}>
                      {t.symbol ? t.symbol.replace("usdt", "").toUpperCase() : "COIN"}
                    </span>
                  </td>
                  <td style={{ textAlign: "right" }}>${formatPrice(t.buyPrice)}</td>
                  <td style={{ textAlign: "right" }}>${formatPrice(t.sellPrice)}</td>
                  <td style={{ textAlign: "right" }}>${t.amount.toFixed(2)}</td>
                  <td
                    style={{
                      textAlign: "right",
                      fontWeight: 700,
                      color: t.pnl >= 0 ? "#16a34a" : "#dc2626",
                    }}
                  >
                    {t.pnl >= 0 ? "+" : ""}${t.pnl.toFixed(2)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ProfileModal({ isOpen, onClose, user, tradeHistory, totalInvested, totalPnl, onLogout }) {
  if (!isOpen) return null;

  const winCount = tradeHistory.filter((t) => t.pnl >= 0).length;
  const winRate = tradeHistory.length > 0 ? (winCount / tradeHistory.length) * 100 : 0;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-card profile-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h3 className="modal-title">Trader Profile</h3>
            <p className="modal-subtitle">{user ? `@${user.username}` : "Portfolio overview and trade logs"}</p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button
              type="button"
              className="btn-solid-logout"
              onClick={() => { onClose(); onLogout(); }}
              title="Sign Out of Account"
            >
              Sign Out
            </button>
            <button className="modal-close" onClick={onClose}>X</button>
          </div>
        </div>

        <div className="profile-stats-grid">
          <div className="profile-stat-box">
            <span className="stat-label">TOTAL INVESTED</span>
            <span className="stat-value">${totalInvested.toFixed(2)}</span>
          </div>
          <div className="profile-stat-box">
            <span className="stat-label">REALIZED PnL</span>
            <span className={`stat-value ${totalPnl >= 0 ? "text-green" : "text-red"}`}>
              {totalPnl >= 0 ? "+" : ""}${totalPnl.toFixed(2)}
            </span>
          </div>
          <div className="profile-stat-box">
            <span className="stat-label">COMPLETED TRADES</span>
            <span className="stat-value">{tradeHistory.length}</span>
          </div>
          <div className="profile-stat-box">
            <span className="stat-label">WIN RATE</span>
            <span className="stat-value">{winRate.toFixed(0)}%</span>
          </div>
        </div>

        <div className="profile-history-section">
          <TradeHistoryTable tradeHistory={tradeHistory} />
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [user, setUser] = useState(null);

  const [history, setHistory] = useState(
    Object.fromEntries(COINS.map((c) => [c.symbol, []]))
  );

  const [trade, setTrade] = useState({
    symbol: null,
    buyPrice: null,
    amount: 0,
    coinsBought: null,
    active: false,
    startTime: null,
  });

  const [tradeHistory, setTradeHistory] = useState(() => {
    const savedUser = localStorage.getItem("crypto_dash_user");
    return savedUser && savedUser.tradeHistory ? JSON.parse(savedUser).tradeHistory : [];
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const priceBuffer = useRef({});
  const tradeAction$ = useRef(new Subject());

  useEffect(() => {
    const streams = COINS.map((c) => `${c.symbol}@trade`).join("/");
    const socketUrl = `wss://stream.binance.com:9443/stream?streams=${streams}`;

    const ws$ = webSocket(socketUrl);

    const wsSubscription = ws$
      .pipe(
        catchError((err) => {
          console.error("RxJS WebSocket stream error:", err);
          return [];
        })
      )
      .subscribe((event) => {
        if (event && event.data) {
          const { s, p } = event.data;
          if (s && p) {
            priceBuffer.current[s.toLowerCase()] = parseFloat(p);
          }
        }
      });

    const timerSubscription = interval(1000).subscribe(() => {
      const now = Date.now();
      setHistory((prev) => {
        const next = { ...prev };
        COINS.forEach(({ symbol }) => {
          const lastPoint = prev[symbol]?.at(-1);
          const bufferedPrice = priceBuffer.current[symbol];

          const point = bufferedPrice !== undefined
            ? { time: now, price: bufferedPrice }
            : lastPoint
              ? { time: now, price: lastPoint.price }
              : null;

          if (!point) return;
          next[symbol] = [...(prev[symbol] || []), point].slice(-MAX_POINTS);
        });

        priceBuffer.current = {};
        return next;
      });
    });

    const tradeSubscription = tradeAction$.current.subscribe((action) => {
      if (action.type === "BUY") {
        setTrade({
          symbol: action.symbol,
          buyPrice: action.price,
          amount: action.amount,
          coinsBought: action.coinsBought,
          active: true,
          startTime: action.startTime || Date.now(),
        });
        setIsModalOpen(false);
      } else if (action.type === "SELL") {
        const completedTrade = {
          symbol: action.trade.symbol,
          buyPrice: action.trade.buyPrice,
          sellPrice: action.currentPrice,
          amount: action.trade.amount,
          coinsBought: action.trade.coinsBought,
          pnl: action.pnl,
        };

        setTradeHistory((prev) => [...prev, completedTrade]);

        const token = localStorage.getItem("crypto_dash_token");
        if (token) {
          fetch("/api/trade", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ trade: completedTrade }),
          }).catch((err) => console.error("Failed to sync trade to MongoDB:", err));
        }

        setTrade({
          symbol: null,
          buyPrice: null,
          amount: 0,
          coinsBought: null,
          active: false,
          startTime: null,
        });
      }
    });

    return () => {
      wsSubscription.unsubscribe();
      timerSubscription.unsubscribe();
      tradeSubscription.unsubscribe();
    };
  }, []);

  const handleLoginSuccess = (userData) => {
    setUser(userData);
    setTradeHistory(userData.tradeHistory || []);
  };

  const handleLogout = () => {
    localStorage.removeItem("crypto_dash_token");
    localStorage.removeItem("crypto_dash_user");
    setUser(null);
  };

  if (!user) {
    return <AuthPage onLoginSuccess={handleLoginSuccess} />;
  }

  const activeCoin = trade.active ? COINS.find((c) => c.symbol === trade.symbol) : null;
  const currentPrice = trade.active ? history[trade.symbol]?.at(-1)?.price : null;

  const pnl = trade.active && currentPrice && trade.buyPrice
    ? ((currentPrice - trade.buyPrice) / trade.buyPrice) * trade.amount
    : null;

  const pnlPct = trade.active && currentPrice && trade.buyPrice
    ? ((currentPrice - trade.buyPrice) / trade.buyPrice) * 100
    : null;

  const totalInvested = tradeHistory.reduce((sum, t) => sum + t.amount, 0);
  const totalPnl = tradeHistory.reduce((sum, t) => sum + t.pnl, 0);

  const handleExecuteTrade = ({ symbol, amount, price, coinsBought }) => {
    tradeAction$.current.next({
      type: "BUY",
      symbol,
      amount,
      price,
      coinsBought,
      startTime: Date.now(),
    });
  };

  const handleSell = () => {
    if (!trade.active || !currentPrice) return;
    tradeAction$.current.next({
      type: "SELL",
      trade,
      currentPrice,
      pnl: pnl ?? 0,
    });
  };

  const visibleCoins = expanded ? COINS.filter((c) => c.symbol === expanded) : COINS;

  return (
    <div className="dashboard-container">
      <HeaderBar
        user={user}
        isTradeActive={trade.active}
        onOpenModal={() => setIsModalOpen(true)}
        onOpenProfile={() => setIsProfileModalOpen(true)}
      />

      <TradeModal
        coins={COINS}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        history={history}
        onExecuteTrade={handleExecuteTrade}
      />

      <ProfileModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        user={user}
        tradeHistory={tradeHistory}
        totalInvested={totalInvested}
        totalPnl={totalPnl}
        onLogout={handleLogout}
      />

      {trade.active && activeCoin ? (
        <ActiveTradeView
          trade={trade}
          coin={activeCoin}
          history={history[trade.symbol] || []}
          currentPrice={currentPrice}
          pnl={pnl}
          pnlPct={pnlPct}
          onSell={handleSell}
        />
      ) : (
        <div
          className="charts-grid"
          style={{
            gridTemplateColumns: expanded ? "1fr" : "1fr 1fr",
            gridTemplateRows: expanded ? "1fr" : "1fr 1fr",
          }}
        >
          {visibleCoins.map((coin) => (
            <CoinCard
              key={coin.symbol}
              coin={coin}
              history={history[coin.symbol] || []}
              expanded={expanded === coin.symbol}
              onExpand={setExpanded}
            />
          ))}
        </div>
      )}
    </div>
  );
}